/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import { redirect, useLocation } from 'react-router-dom'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField
} from '@material-ui/core'
import { Cancel, NoteAdd } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import { Third } from '../../../schemas/Third'
import ThirdService from '../../../services/Third'
import Utils from '../../../utils'
import { urls } from '../../../urls'
import PageContainer from '../Common/PageContainer'
import FormActions from '../Common/FormActions'
import SelectField from '../Common/SelectField'
import ciudadesPorDepartamento from '../../../helpers/ciudades_por_departamento.json'

interface Municipio {
  value: number
  label: string
}

interface Departamento {
  value: string
  label: string
  departamento: string
  municipios: Municipio[]
}

const departmentOptions = ciudadesPorDepartamento as Departamento[]

const findDepartmentByCityCode = (cityCode: string) => {
  return departmentOptions.find(dept =>
    dept.municipios.some(municipio => String(municipio.value) === cityCode)
  )
}

const getInitialLocationState = () => {
  const firstDepartment = departmentOptions[0]
  const firstCity = firstDepartment?.municipios[0]

  return {
    selectedDepartment: firstDepartment?.departamento ?? '',
    cityOptions: firstDepartment?.municipios ?? [],
    city: firstCity
      ? { code: String(firstCity.value), description: firstCity.label }
      : undefined
  }
}

const initialLocation = getInitialLocationState()

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    description: 'Crear terceros'
  },
  body: {
    field: {
      organizationType: {
        name: 'organizationType',
        helperText: 'Escoja un tipo de persona',
        options: [
          { description: 'Jurídica', code: '1' },
          { description: 'Natural', code: '2' }
        ]
      },
      documentType: {
        name: 'documentType',
        helperText: 'Escoja el tipo de documento',
        options: {
          one: [
            { description: 'NIT', code: '6' }
          ],
          two: [
            { description: 'Registro civil', code: '1' },
            { description: 'Tarjeta de identidad', code: '2' },
            { description: 'Cédula de ciudadanía', code: '3' },
            { description: 'Cédula de extranjería', code: '5' },
            { description: 'Pasaporte', code: '7' },
            { description: 'Documento de identificación extranjero', code: '8' }
          ]
        }
      },
      liabilityType: {
        name: 'liabilityType',
        helperText: 'Escoja el tipo de responsabilidad',
        options: [
          { description: 'Gran contribuyente', code: '7' },
          { description: 'Autorretenedor', code: '9' },
          { description: 'Agente de retención en el impuesto sobre las ventas', code: '14' },
          { description: 'Régimen Simple de Tributación – SIMPLE', code: '112' },
          { description: 'No responsable', code: '117' }
        ]
      },
      regimeType: {
        name: 'regimeType',
        helperText: 'Escoja el tipo de régimen',
        options: [
          { description: 'Responsable de IVA', code: '1' },
          { description: 'No responsable de IVA', code: '2' }
        ]
      },
      document: {
        name: 'document',
        helperText: {
          main: 'Escriba el número de documento',
          error: 'El documento ingresado no tiene el formato correcto'
        },
        placeholder: 'Documento'
      },
      dv: {
        name: 'dv',
        helperText: 'DV',
        placeholder: 'DV'
      },
      name: {
        one: {
          name: {
            name: 'name',
            helperText: 'Escriba el/los nombre(s) de la persona',
            placeholder: 'Nombre'
          },
          lastname: {
            name: 'lastname',
            helperText: 'Escriba el/los apellido(s) de la persona',
            placeholder: 'Apellido(s)'
          }
        },
        two: {
          businessName: {
            name: 'businessName',
            helperText: 'Escriba el nombre de la empresa',
            placeholder: 'Razón social'
          }
        }
      },
      email: {
        name: 'email',
        helperText: {
          main: 'Escriba el email',
          error: 'El correo ingresado no tiene el formato correcto'
        },
        placeholder: 'Email'
      },
      phone: {
        name: 'phone',
        helperText: 'Escriba el número telefónico',
        placeholder: 'Número telefónico'
      },
      address: {
        name: 'address',
        helperText: 'Escriba la dirección',
        placeholder: 'Dirección'
      },
      department: {
        name: 'department',
        helperText: 'Escoja el departamento'
      },
      city: {
        name: 'city',
        helperText: 'Ciudad o municipio'
      }
    }
  }
}

// ------------ Init state -----------
interface State {
  form: Third
  errorOnMail: boolean
  errorOnDocument: boolean
  selectedDepartment: string
  cityOptions: Municipio[]
  loading: boolean
  isEdit: boolean
}

const initState: State = {
  form: {
    document: '',
    dv: undefined,
    documentType: {
      code: '',
      description: ''
    },
    organizationType: {
      code: '',
      description: ''
    },
    liabilityType: {
      code: '',
      description: ''
    },
    regimeType: {
      code: '',
      description: ''
    },
    name: undefined,
    lastname: undefined,
    businessName: undefined,
    phone: '',
    address: '',
    city: initialLocation.city,
    email: ''
  },
  errorOnMail: false,
  errorOnDocument: false,
  selectedDepartment: initialLocation.selectedDepartment,
  cityOptions: initialLocation.cityOptions,
  loading: false,
  isEdit: false
}

export default function ThirdForm() {
  const classes = useStyles()
  const { search } = useLocation()
  
  const [state, setState] = useState<State>(initState)

  React.useEffect(() => {
    async function loadData() {
      setState(prevState => ({
        ...prevState,
        loading: true
      }))

      const document = Utils.getIdFromUrl(search)

      if (document) {
        const thirdService = new ThirdService()
        const third = await thirdService.getThirdByDocument(document)
        if (third) {
          const cityCode = third.city?.code ?? ''
          const departmentWithCity = findDepartmentByCityCode(cityCode)
          const fallbackDepartment = departmentOptions[0]

          setState(prevState => ({
            ...prevState,
            form: third,
            selectedDepartment: departmentWithCity?.departamento ?? fallbackDepartment.departamento,
            cityOptions: departmentWithCity?.municipios ?? fallbackDepartment.municipios,
            loading: false,
            isEdit: true
          }))
        } else {
          setState(prevState => ({
            ...prevState,
            loading: false
          }))
        }
      } else {
        setState(prevState => ({
          ...prevState,
          loading: false
        }))
      }
    }
    loadData()
  }, [])

  // HandleChange is the handler to update the state
  // of the state, while the user change the options 
  // on an input select.
  const handleChangeSelect = (name: string, item: { code: string, description: string }) => {
    setState({
      ...state,
      form:{
        ...state.form,
        [name]: item
      }
    })
  }

  const handleDepartmentChange = (departamento: string) => {
    const selectedDept = departmentOptions.find(dept => dept.departamento === departamento)
    if (!selectedDept) return

    const firstCity = selectedDept.municipios[0]

    setState({
      ...state,
      selectedDepartment: departamento,
      cityOptions: selectedDept.municipios,
      form: {
        ...state.form,
        city: firstCity
          ? { code: String(firstCity.value), description: firstCity.label }
          : undefined
      }
    })
  }

  const handleCityChange = (municipio: Municipio) => {
    setState({
      ...state,
      form: {
        ...state.form,
        city: {
          code: String(municipio.value),
          description: municipio.label
        }
      }
    })
  }

  // HandleChange is the handler to update the state
  // of the state, while the user writes on an input.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    // Validaciones
    let errorOnDocument = state.errorOnDocument
    let errorOnMail = state.errorOnMail

    if (name === 'document') {
      const regex = /^[0-9]+$/
      errorOnDocument = !regex.test(value)
    }

    if (name === 'email') {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      errorOnMail = !regex.test(value)
    }

    setState({
      ...state,
      form: {
        ...state.form,
        [name]: value,
      },
      errorOnMail: errorOnMail,
      errorOnDocument: errorOnDocument
    })
  }

  // HandleSubmit is the handler to verify and send
  // the state to redux (redux send the info to backend)
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
  
    // validaciones
    if (state.errorOnDocument) {
      return toast.error('Documento con formato incorrecto')
    }

    if (state.errorOnMail) {
      return toast.error('Mail con formato incorrecto')
    }

    try {
      const thirdService = new ThirdService()
      let thirdCreated: any = {}

      if (state.isEdit) {
        thirdCreated = await thirdService.updateThird(state.form)
      } else {
        thirdCreated = await thirdService.saveThird(state.form)
      }
      setState({
        ...initState,
        loading: false
      })

      redirect(urls.app.main.third.form)

      return toast.success(`El tercero ${thirdCreated.document} fue ${state.isEdit ? 'actualizado' : 'creado'} con éxito`)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message)
        }
      }
    }
  }

  const cancel = () => {
    setState(initState)
  }

  return (
    <PageContainer title={texts.header.description}>

      {/* ----- Body ------*/}
      <form onSubmit={(e) => handleSubmit(e)}>
        <Grid container spacing={3}>

          {/* ----- Form: item thrid type ------*/}
          <Grid item md={state.form.organizationType.code === '2' ? 4 : 3} sm={6} xs={12}>
            <SelectField
              name={texts.body.field.organizationType.name}
              label="Tipo de persona"
              value={state.form.organizationType.code}
              helperText={texts.body.field.organizationType.helperText}
              disabled={state.isEdit}
            >
              {
                texts.body.field.organizationType.options.map((item) =>
                  <MenuItem
                    key={item.description}
                    value={item.code}
                    onClick={() => handleChangeSelect(texts.body.field.organizationType.name, item)}
                  >
                    {item.description}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: item document type ------*/}
          <Grid item md={state.form.organizationType.code === '2' ? 4 : 3} sm={6} xs={12}>
            <SelectField
              name={texts.body.field.documentType.name}
              label="Tipo de documento"
              value={state.form.documentType.code}
              helperText={texts.body.field.documentType.helperText}
            >
              {
                // this will depend of organizationType field
                state.form.organizationType.code === texts.body.field.organizationType.options[0].code ?
                  texts.body.field.documentType.options.one.map((item) =>
                    <MenuItem
                      key={item.description}
                      value={item.code}
                      onClick={() => handleChangeSelect(texts.body.field.documentType.name, item)}
                    >
                      {item.description}
                    </MenuItem>
                  )
                  :
                  texts.body.field.documentType.options.two.map((item) =>
                    <MenuItem
                      key={item.description}
                      value={item.code}
                      onClick={() => handleChangeSelect(texts.body.field.documentType.name, item)}
                    >
                      {item.description}
                    </MenuItem>
                  )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: item document number ------*/}
          <Grid item md={state.form.organizationType.code === '2' ? 4 : 4} sm={6} xs={state.form.organizationType.code === '2' ? 12 : 8}>
            <TextField
              required={true}
              name={texts.body.field.document.name}
              label={texts.body.field.document.placeholder}
              value={state.form.document}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={state.errorOnDocument ? texts.body.field.document.helperText.error : texts.body.field.document.helperText.main}
              disabled={state.isEdit}
              error={state.errorOnDocument}
              inputProps={{ inputMode: 'numeric' }}
            />
          </Grid>
          {
            /*
            In this section the name, secondName, lastName and businessName
            fields are conditioned to the organizationType field.
            If typeThird field is NATURAL then the name, secondName and lastName
            fields will be availabe while businessName field will be unavailable,
            else if typeThird field is JURIDICA then the businessName field will
            be available while the name, secondName and lastName fields will be
            unavailable
            */

            state.form.organizationType.code === '2'
              ?
              <React.Fragment>
                <Grid item md={6} sm={6} xs={12}>
                  <TextField
                    required={true}
                    name={texts.body.field.name.one.name.name}
                    label={texts.body.field.name.one.name.placeholder}
                    value={state.form.name ?? ''}
                    variant='outlined'
                    fullWidth
                    onChange={handleChange}
                    helperText={texts.body.field.name.one.name.helperText}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <TextField
                    required={true}
                    name={texts.body.field.name.one.lastname.name}
                    label={texts.body.field.name.one.lastname.placeholder}
                    value={state.form.lastname ?? ''}
                    variant='outlined'
                    fullWidth
                    onChange={handleChange}
                    helperText={texts.body.field.name.one.lastname.helperText}
                  />
                </Grid>
              </React.Fragment>
              :
              <React.Fragment>
                {/* ----- Form: item document dv ------*/}
                <Grid item md={2} sm={6} xs={4}>
                  <TextField
                    required={true}
                    name={texts.body.field.dv.name}
                    label={texts.body.field.dv.placeholder}
                    value={state.form.dv ?? ''}
                    variant='outlined'
                    fullWidth
                    onChange={handleChange}
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
                <Grid item md={12} sm={12} xs={12}>
                  <TextField
                    required={true}
                    name={texts.body.field.name.two.businessName.name}
                    label={texts.body.field.name.two.businessName.placeholder}
                    value={state.form.businessName ?? ''}
                    variant='outlined'
                    fullWidth
                    onChange={handleChange}
                    helperText={texts.body.field.name.two.businessName.helperText}
                  />
                </Grid>
              </React.Fragment>
          }

          {/* ----- Form: item liability type ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <SelectField
              name={texts.body.field.liabilityType.name}
              label="Tipo de responsabilidad"
              value={state.form.liabilityType.code}
              helperText={texts.body.field.liabilityType.helperText}
            >
              {
                texts.body.field.liabilityType.options.map((item) =>
                  <MenuItem
                    key={item.description}
                    value={item.code}
                    onClick={() => handleChangeSelect(texts.body.field.liabilityType.name, item)}
                  >
                    {item.description}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: item regime type ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <SelectField
              name={texts.body.field.regimeType.name}
              label="Tipo de régimen"
              value={state.form.regimeType.code}
              helperText={texts.body.field.regimeType.helperText}
            >
              {
                texts.body.field.regimeType.options.map((item) =>
                  <MenuItem
                    key={item.description}
                    value={item.code}
                    onClick={() => handleChangeSelect(texts.body.field.regimeType.name, item)}
                  >
                    {item.description}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: item email ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <TextField
              name={texts.body.field.email.name}
              label={texts.body.field.email.placeholder}
              value={state.form.email}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={state.errorOnMail ? texts.body.field.email.helperText.error : texts.body.field.email.helperText.main}
              error={state.errorOnMail}
              inputProps={{ inputMode: 'email' }}
            />
          </Grid>

          {/* ----- Form: item phone ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <TextField
              name={texts.body.field.phone.name}
              label={texts.body.field.phone.placeholder}
              value={state.form.phone}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.phone.helperText}
              inputProps={{ inputMode: 'tel' }}
            />
          </Grid>

          {/* ----- Form: item address ------*/}
          <Grid item md={6} sm={12} xs={12}>
            <TextField
              name={texts.body.field.address.name}
              label={texts.body.field.address.placeholder}
              value={state.form.address}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.address.helperText}
            />
          </Grid>

          {/* ----- Form: Department ------*/}
          <Grid item md={3} sm={6} xs={12}>
            <SelectField
              name={texts.body.field.department.name}
              label="Departamento"
              value={state.selectedDepartment}
              helperText={texts.body.field.department.helperText}
            >
              {
                departmentOptions.map((item) =>
                  <MenuItem
                    key={item.departamento}
                    value={item.departamento}
                    onClick={() => handleDepartmentChange(item.departamento)}
                  >
                    {item.departamento}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: City ------*/}
          <Grid item md={3} sm={6} xs={12}>
            <SelectField
              name={texts.body.field.city.name}
              label="Ciudad"
              value={state.form.city?.code ?? ''}
              helperText={texts.body.field.city.helperText}
            >
              {
                state.cityOptions.map((item) =>
                  <MenuItem
                    key={item.value}
                    value={String(item.value)}
                    onClick={() => handleCityChange(item)}
                  >
                    {item.label}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

        </Grid>

        <FormActions>
          <Button type="button" variant="outlined" color="secondary" startIcon={<Cancel />} onClick={cancel}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary" startIcon={<NoteAdd />}>
            {state.isEdit ? 'Actualizar' : 'Guardar'}
          </Button>
        </FormActions>
      </form>
      {
        state.loading ?
        (
          <div className={classes.loadingContainer}>
            <Box>
              <CircularProgress/>
            </Box>
          </div>
        ) : ''
      }
    </PageContainer>
  )
}
